import {
  ClipboardListIcon,
  DnaIcon,
  FileTextIcon,
  FingerprintIcon,
  FlaskConicalIcon,
  GraduationCapIcon,
  LaptopIcon,
  MicroscopeIcon,
  SearchIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  classroom: string;
  items: string[];
  result: string;
  icon: LucideIcon;
};

export const modules: Module[] = [
  {
    number: '1',
    title: 'Fundamentos de la Criminalística',
    subtitle: 'La base que tus alumnos necesitan para entender todo lo demás',
    classroom:
      'El primer contacto con la criminalística puede ser el más transformador o el más confuso. Este tomo convierte un tema técnico en algo comprensible y relevante para tus alumnos desde el primer día.',
    items: [
      'Concepto, objeto y objetivos de la Criminalística',
      'Evolución histórica de la disciplina',
      'Principios fundamentales',
      'Criminalística de campo y de laboratorio',
      'Método científico aplicado al análisis criminalístico',
      'Indicios, evidencias y elementos materiales',
      'Principales disciplinas forenses',
      'Rol de los especialistas',
      'Introducción al razonamiento criminalístico',
      'Aplicaciones educativas y casos de análisis',
    ],
    result:
      'Tus alumnos entienden la criminalística como una disciplina práctica y relevante, no como un conjunto de términos imposibles de memorizar.',
    icon: MicroscopeIcon,
  },
  {
    number: '2',
    title: 'Investigación del Lugar de los Hechos',
    subtitle: 'Todo lo que necesitan saber sobre investigación de escenas',
    classroom:
      'Este es uno de los puntos donde más se traban los alumnos. Este tomo te da el material listo para que comprendan la investigación del lugar de los hechos sin sobrecarga técnica.',
    items: [
      'Concepto y características del lugar de los hechos',
      'Diferentes tipos de escenarios',
      'Observación y análisis inicial',
      'Preservación y documentación',
      'Reconocimiento de indicios',
      'Registro y descripción de elementos',
      'Principios generales de recolección y conservación',
      'Cadena de custodia',
      'Errores frecuentes y contaminación',
      'Situaciones ficticias para análisis académico',
      '12 casos prácticos de investigación de escenas',
    ],
    result: 'Tus alumnos aprenden los principios que explican cómo se investiga correctamente una escena.',
    icon: SearchIcon,
  },
  {
    number: '3',
    title: 'Indicios, Evidencias y Ciencias Forenses',
    subtitle: 'Cómo se interpretan los diferentes elementos de una investigación',
    classroom:
      'La interpretación de indicios es fascinante pero compleja. Este tomo te da todo lo que necesitas para que tus alumnos comprendan este proceso.',
    items: [
      'Diferencias entre indicio y evidencia',
      'Clasificación de indicios',
      'Evidencia física, biológica, química y documental',
      'Valor científico de la evidencia',
      'Asociación y comparación de elementos',
      'Trazabilidad y cadena de custodia',
      'Introducción a las ciencias forenses',
      'Interpretación de resultados',
      'Formulación y contraste de hipótesis',
      'Ejercicios de razonamiento criminalístico',
      '15 casos prácticos de análisis de evidencias',
    ],
    result: 'Tus alumnos comprenden cómo los indicios se transforman en evidencias confiables.',
    icon: ClipboardListIcon,
  },
  {
    number: '4',
    title: 'Dactiloscopía, Huellas y Sistemas de Identificación',
    subtitle: 'Un recorrido por los fundamentos científicos de la identificación',
    classroom: 'La identificación humana es fundamental pero técnica. Este tomo lo explica de forma clara y accesible.',
    items: [
      'Introducción a la identificación',
      'Fundamentos de la dactiloscopía',
      'Características de las huellas dactilares',
      'Principales patrones y particularidades',
      'Huellas visibles, plásticas y latentes',
      'Principios generales de análisis y comparación',
      'Otros sistemas de identificación',
      'Alcances y limitaciones',
      'Innovaciones tecnológicas',
      'Actividades educativas de observación y análisis',
      '12 casos prácticos de identificación',
    ],
    result: 'Tus alumnos entienden cómo funcionan los sistemas de identificación y por qué son confiables.',
    icon: FingerprintIcon,
  },
  {
    number: '5',
    title: 'Biología y Genética Forense',
    subtitle: 'Los fundamentos biológicos y genéticos en la investigación',
    classroom: 'La biología forense es poderosa pero compleja. Este tomo la hace comprensible.',
    items: [
      'Introducción a la biología forense',
      'Células, tejidos y material biológico',
      'Fundamentos del ADN',
      'Variabilidad genética',
      'Perfil genético y su interpretación conceptual',
      'Tipos de evidencia biológica',
      'Contaminación y conservación',
      'Comparación e interpretación de resultados',
      'Alcances y limitaciones de la genética forense',
      'Casos educativos y dilemas para el aula',
      '15 casos prácticos de biología forense',
    ],
    result: 'Tus alumnos comprenden cómo el ADN y la biología forense contribuyen a las investigaciones.',
    icon: DnaIcon,
  },
  {
    number: '6',
    title: 'Documentos, Escritura y Análisis Forense',
    subtitle: 'El estudio científico de documentos y escritura',
    classroom: 'El análisis documental es importante pero especializado. Este tomo lo hace accesible.',
    items: [
      'Introducción al análisis documental',
      'Tipos de documentos',
      'Características generales de la escritura',
      'Elementos utilizados en procesos comparativos',
      'Firmas y manuscritos',
      'Alteraciones documentales',
      'Tintas, soportes y sistemas de impresión',
      'Documentos físicos y digitales',
      'Alcances y limitaciones del análisis',
      'Casos y ejercicios educativos',
      '12 casos prácticos de documentología',
    ],
    result: 'Tus alumnos entienden cómo se analizan los documentos como evidencia forense.',
    icon: FileTextIcon,
  },
  {
    number: '7',
    title: 'Química y Toxicología Forense',
    subtitle: 'La química en la investigación criminal',
    classroom: 'La química forense es técnica pero importante. Este tomo la explica claramente.',
    items: [
      'Fundamentos de química forense',
      'Principios de toxicología',
      'Sustancias y muestras de interés forense',
      'Conceptos de dosis, exposición y toxicidad',
      'Análisis e interpretación científica',
      'Instrumentación y técnicas desde una perspectiva conceptual',
      'Calidad y confiabilidad de resultados',
      'Limitaciones de los análisis',
      'Prevención y seguridad',
      'Casos ficticios para interpretación académica',
      '12 casos prácticos de química y toxicología',
    ],
    result: 'Tus alumnos comprenden cómo la química y toxicología se aplican en investigaciones.',
    icon: FlaskConicalIcon,
  },
  {
    number: '8',
    title: 'Criminalística Digital y Nuevas Tecnologías',
    subtitle: 'Una introducción actualizada al impacto de la tecnología',
    classroom: 'La criminalística digital es actual y necesaria. Este tomo la aborda completamente.',
    items: [
      'Fundamentos de criminalística digital',
      'Concepto de evidencia digital',
      'Dispositivos y fuentes de información',
      'Integridad y trazabilidad de datos',
      'Metadatos y registros digitales',
      'Principios de preservación',
      'Tecnologías aplicadas a las ciencias forenses',
      'Inteligencia artificial y nuevas herramientas',
      'Límites, ética y privacidad',
      'Tendencias y desafíos actuales',
      '15 casos prácticos de criminalística digital',
    ],
    result: 'Tus alumnos entienden el rol creciente de la tecnología en la criminalística moderna.',
    icon: LaptopIcon,
  },
  {
    number: '9',
    title: 'Enseñanza de la Criminalística: Planificación, Actividades y Casos',
    subtitle: 'Tu guía completa como docente',
    classroom: 'Este tomo te da exactamente lo que necesitas para planificar, organizar y evaluar todo el año.',
    items: [
      'Estrategias para enseñar Criminalística',
      'Organización y planificación de contenidos',
      'Diseño de clases',
      'Aprendizaje basado en problemas',
      'Trabajo con casos ficticios',
      'Actividades de análisis e investigación',
      'Debate y argumentación',
      'Evaluación del aprendizaje',
      'Desarrollo del pensamiento crítico',
      'Propuestas y proyectos para el aula',
      '10 proyectos educativos listos para adaptar',
    ],
    result: 'Nunca más te preguntas “¿qué doy hoy?”. Todo está planificado y contextualizado.',
    icon: GraduationCapIcon,
  },
];
